import { Controller } from '@hotwired/stimulus';
import { queueDisplaySave } from 'helpers/display_saves';

// Page-wide toggles for the goal cards' panels on a progress report: one button per panel
// (details / status / comments). A click opens that panel on every goal card when any
// card still has it closed, and closes it on every card otherwise. The panels are Bootstrap
// collapses, and a master drives them through the plugin so they slide the way a card's own pill
// slides them (the stylesheet keeps the slide short); the plugin keeps each pill in step
// (aria-expanded and the collapsed class). Without the plugin on the page the state is applied
// directly instead. A master reads as pressed (aria-pressed) while every card shows its panel,
// and re-syncs after a card's own pill is used.
//
// On the fill page the goal rows start closed and the goal picker may hide a card, and a panel
// opened inside a closed row, or on a hidden card, is out of sight. So a master reads from the
// goals on show alone, and one that opens a panel opens their closed rows first, directly through
// each row's own controller as the heading's caret does (goals-v3-row#applyOpen, no animation, so
// the sweep is never half-dropped by Bootstrap's in-transition guard), then says so (rows-opened)
// for the caret to follow. The signed page's rows start open, so nothing changes there.
//
// What stands open is what the PDF prints, goal by goal: when the element carries a url value,
// every master click and every pill (the plugin's shown/hidden events) saves the state of each
// goal card on the page to the report (PATCH, JSON). The page has changed by then, so a save that
// does not go through (a session that ended, a change the report refused) is said on the page
// (the notice target) rather than left for the PDF to contradict. The payload names each card by
// its data-goals-report-goal id and each of its panels as open or closed. A master click also
// speaks for the panels a card does not have, so a goal that gains one later (a first comment,
// say) follows the master.
//
// Targets:
//   button - each master (data-goals-report-panels-panel-param = details|status|comments)
//   notice - the alert shown once a save has failed (hidden until then)
// Values:
//   url - where to save the state (absent when the viewer may not change it)
export default class extends Controller {
  static targets = ['button', 'notice'];
  static values = { url: String };

  connect() {
    // The panels a master is sliding right now: their shown/hidden events keep the masters in
    // step but queue no save of their own, since the master's one save already carries the page.
    this.sweeping = new Set();
    this.onCollapseEvent = (event) => {
      if (!event.target.dataset || !event.target.dataset.panel) return;
      this.sync();
      if (this.sweeping.delete(event.target)) return;
      this.save();
    };
    const $ = this.jquery();
    if ($) $(this.element).on('shown.bs.collapse hidden.bs.collapse', this.onCollapseEvent);
    this.sync();
  }

  disconnect() {
    clearTimeout(this.sweepTimer);
    const $ = this.jquery();
    if ($) $(this.element).off('shown.bs.collapse hidden.bs.collapse', this.onCollapseEvent);
  }

  // With nothing on the page to toggle (no goal has that panel yet) the master still flips, so
  // the choice of what to print can be made ahead of the content.
  toggle(event) {
    const { panel } = event.params;
    const panels = this.panels(panel);
    let open;
    if (panels.length) {
      open = panels.some((element) => !element.classList.contains('show'));
      // The rows first, so the panel opens in view.
      if (open) this.openRows();
      // One save for the sweep: each panel that moves fires its own collapse event afterwards,
      // which must not queue a save per panel (26 row locks for one click on a 25-goal report).
      // A panel the plugin leaves alone (already there, or still sliding) fires none, so the set
      // is cleared once the slides are over rather than waiting on it.
      panels
        .filter((element) => element.classList.contains('show') !== open)
        .forEach((element) => this.sweeping.add(element));
      panels.forEach((element) => this.slide(element, open));
      clearTimeout(this.sweepTimer);
      this.sweepTimer = setTimeout(() => this.sweeping.clear(), 1000);
    } else {
      const button = event.currentTarget;
      open = button.getAttribute('aria-pressed') !== 'true';
      this.press(button, open);
    }
    this.sync();
    this.save({ [panel]: open });
  }

  // Every goal card on the page: goal id => panel key => open. `spoken` adds the panels a master
  // just decided for every card, present on the card or not.
  state(spoken = {}) {
    const state = {};
    this.element.querySelectorAll('[data-goals-report-goal]').forEach((card) => {
      const panels = {};
      card.querySelectorAll('[data-panel]').forEach((panel) => {
        panels[panel.dataset.panel] = panel.classList.contains('show');
      });
      state[card.dataset.goalsReportGoal] = { ...panels, ...spoken };
    });
    return state;
  }

  // One at a time with the page's other display saves (helpers/display_saves), every card's panels
  // read when the save's turn comes, so it carries the page as it stands by then.
  save(spoken = {}) {
    if (!this.hasUrlValue || !this.urlValue) return;
    const url = this.urlValue;
    queueDisplaySave(() => {
      const token = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token ? token.getAttribute('content') : '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ panels: this.state(spoken) }),
      });
    })
      .then((response) => {
        // A redirect is the sign-in page: the session ended and nothing was saved.
        if (!response.ok || response.redirected) throw new Error(`Panels not saved (${response.status})`);
      })
      .catch(() => this.failed());
  }

  // The page and the report now differ; say so until the page is reloaded.
  failed() {
    this.noticeTargets.forEach((element) => {
      element.hidden = false;
    });
  }

  // The panels of the goals on show: a card the goal picker hides carries the hidden attribute.
  panels(key) {
    return Array.from(this.element.querySelectorAll(`[data-panel="${key}"]`)).filter((panel) => {
      const card = panel.closest('[data-goals-report-goal]');
      return !card || !card.hidden;
    });
  }

  // Every closed row among the goals on show, opened through its own controller.
  openRows() {
    const opened = this.cards().filter((card) => {
      const row = this.application.getControllerForElementAndIdentifier(card, 'goals-v3-row');
      if (!row || this.rowOpen(card)) return false;
      row.applyOpen(true);
      return true;
    });
    if (opened.length) this.dispatch('rows-opened');
  }

  cards() {
    return Array.from(this.element.querySelectorAll('[data-goals-report-goal]')).filter((card) => !card.hidden);
  }

  rowOpen(card) {
    const detail = card.querySelector('[data-goals-v3-row-target~="detail"]');
    return Boolean(detail && detail.classList.contains('show'));
  }

  // Through Bootstrap's collapse, so the panel slides as it does from its own pill and the plugin
  // keeps the pill in step; a panel already where it is going, or one still sliding, is left to
  // the plugin. The state is applied directly where the plugin is not on the page.
  slide(panel, open) {
    const $ = this.jquery();
    if (!$ || typeof $.fn.collapse !== 'function') {
      this.apply(panel, open);
      return;
    }
    $(panel).collapse(open ? 'show' : 'hide');
  }

  apply(panel, open) {
    panel.classList.remove('collapsing');
    panel.classList.add('collapse');
    panel.classList.toggle('show', open);
    panel.style.height = '';
    if (!panel.id) return;
    this.element
      .querySelectorAll(
        `[data-toggle="collapse"][href="#${panel.id}"], [data-toggle="collapse"][data-target="#${panel.id}"]`
      )
      .forEach((pill) => {
        pill.setAttribute('aria-expanded', open ? 'true' : 'false');
        pill.classList.toggle('collapsed', !open);
      });
  }

  // A master with nothing to toggle on this page keeps whatever state it holds (rendered from the
  // saved choice, or flipped by a click); the others read from the panels.
  sync() {
    this.buttonTargets.forEach((button) => {
      const panels = this.panels(button.dataset.goalsReportPanelsPanelParam);
      if (panels.length === 0) return;
      this.press(
        button,
        panels.every((element) => element.classList.contains('show'))
      );
    });
  }

  press(button, pressed) {
    button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    button.classList.toggle('active', pressed);
  }

  jquery() {
    return typeof window !== 'undefined' && typeof window.$ === 'function' ? window.$ : null;
  }
}
